import * as caseActions from './actions';

import { CaseActionTypes, CaseState } from './types';
import { CaseResponse, UserAccountResponse, PartyResponse, CaseTypeCodes, CaseCountResponse } from './model';

import { ActionType } from 'typesafe-actions';
import { Reducer } from 'redux';

const initialState: CaseState = {
  cases: [],
  offlineCases: [],
  caseSearch: {
    fetchSize: 15,
    offset: 0,
    sortColumn: 'START_DATE',
    sortDirection: 'DESC',
    caseCode: '',
    taskType: '',
    archived: false,
    agendaRef: '',
    policyHolder: '',
    insuredPersonData: '',
    causeOfDamage: '',
    insurer: '',
    sizeOfDamage: '',
    againstParty: '',
    hasDocumentForApproval: false,
    hasUnassignedUser: false,
    hasMissingHonoraryNote: false,
    assignedToPartyId: 0,
    assignedParties: [],
    parties: [],
    childParties: [],
    selectedPartyId: 0,
    selectedChildPartyId: 0,
    inProgressOnly: false
  },
   caseCount: {
    countOfArchived: 0,
    countOfOpened: 0,
    countOfHasDocForApproval: 0,
    countOfHonoraryNoteMissing: 0,
    countOfUnassigned: 0
  },
  requestSent: false,
  searchBarVisible: false,
  refusalModalVisible: false,
  archiveModalVisible: false,
  selectedCase: {
    instructionId: 0,
    taskType: '',
    visitDate: '', // Use an ISO string or null
    caseCode: '',
    expertName: '',
    damageCauseIcon: '',
    insuredPersonName: '',
    causeOfDamage: '',
    damageCauseDescription: '',
    insurer: '',
    sizeOfDamage: '',
    againstParty: '',
    claimNotificationId: 0,
    taskId: 0,
    refuseReasonTC: '',
    refuseReasonText: '',
    archived: false,
    assignedUserId: 0,
    offline: false,
    damageIcon: '',
    approved: false,
    hasHonoraryNote: false,
    isRead: false,
    policySubTypeTC: '',
    createdOn: '', // Use an ISO string or null
    riskAddressId: 0,
    riskCity: '',
    riskStreet: '',
    riskBuildingNumber: '',
    riskBoxNumber: '',
    riskZipCode: '',
    addressId: null,
    telephone: '',
    mobile: '',
    policyHolderCity: '',
    policyHolderStreet: '',
    policyHolderBuildingNumber: '',
    policyHolderBoxNumber: '',
    policyHolderZipCode: '',
    policyHolderLanguageTC: '',
    blocked: true,
    agendaReference: '',
    responsibleInsurerCaseNumber: '',
    responsiblePartyLicensePlate: '',
    responsiblePartyPolicyType: '',
    responsiblePartyInsurerName: '',
    allApproved: false,
    anythingForApproval: false,
    allHnApproved: false,
    anyHnForApproval: false,
    parentPartyDibCode: '',
    parentPartyName: '',
    inProgress: false,
    instructionText: '',
  },
  caseTypeCodes: { refuseReasons: [], taskTypeList: [] },
  showAssignModal: false,
  assignableUsers: [],
  selectedClaimNotificationId: 0,
  selectedAssignedUserId: '',
  searchCase: '',
  showCaseNotFoundMessage: false,
  hasDocumentsForApproval: 0
};
interface SelectedCasePayload {
  selectedCase: CaseResponse;
}
interface AssignableUsersPayload {
  assignableUsers: UserAccountResponse[];
  claimNotificationId: number;
}
interface AssignUserSuccessPayload {
  claimNotificationId: number;
  userId: number;
  expertName: string;
}
interface PartyResponsePayload {
  partyResponse: PartyResponse[];
}
interface FetchCaseForMobileSuccessAction {
  type: string;
  payload: CaseResponse[]; // This ensures the payload is always an array of CaseResponse
}
interface SelectCaseAction {
  type: string;
  payload: { selectedCase: CaseResponse }; 
}
interface RefuseReasonChangeAction {
  type: string;
  payload: string; // Ensure payload is always a string
}
interface RefuseReasonTextChangeAction {
  type: string;
  payload: string; // Ensure payload is always a string
}
interface FetchCaseTypeCodesSuccessAction {
  type: string;
  payload: CaseTypeCodes; // Ensure payload matches the CaseTypeCodes type
}
interface FetchCasesCountSuccessAction {
  type: string;
  payload: CaseCountResponse; // Ensure payload matches the CaseCountResponse type
}
export type CaseAction = ActionType<typeof caseActions>;
const reducer: Reducer<CaseState, CaseAction> = (state = initialState, action): CaseState => {
  switch (action.type) {
    case CaseActionTypes.FETCH_CASES: {
      if (typeof action.payload === 'object' && 'caseSearch' in action.payload) { 
        return { ...state, caseSearch: { ...action.payload.caseSearch } };
      }
      return { ...state };
    }
    case CaseActionTypes.FETCH_CASES_SUCCESS: {
      if (typeof action.payload === 'object' && 'searchClicked' in action.payload) { 
        if (action.payload.searchClicked) {
          if ('cases' in action.payload){
            return { ...state, cases: [ ...action.payload.cases], requestSent: false };
          }
        }
      }
      if (typeof action.payload === 'object' && 'cases' in action.payload) { 
        return { ...state, cases: [...state.cases, ...action.payload.cases], requestSent: false };
      }
      return { ...state };
    }
    case CaseActionTypes.FETCH_CASES_FAIL: {
      return { ...state };
    }
    case CaseActionTypes.FETCH_CASE_FOR_MOBILE_SUCCESS: {
      const fetchCaseForMobileSuccessAction = action as FetchCaseForMobileSuccessAction

      return { 
        ...state, 
        cases: fetchCaseForMobileSuccessAction.payload, 
        selectedCase: fetchCaseForMobileSuccessAction.payload[0], 
        showCaseNotFoundMessage: false, 
        requestSent: false 
      };
    }
    case CaseActionTypes.FETCH_CASE_FOR_MOBILE_FAIL: {
      return { ...state };
    }
    case CaseActionTypes.SET_NO_CASE_FOUND_MESSAGE: {
      return { ...state, cases: [], showCaseNotFoundMessage:  true };
    }
    // RE_CHECK ALL THINGS ABOUT OFFSET!!!
    case CaseActionTypes.CLEAR_CASES: {
      return { ...state, caseSearch: { ...state.caseSearch, offset: 0 }, cases: [], requestSent: true };
    }
    case CaseActionTypes.SHOW_SEARCH_BAR: {
      return { ...state, searchBarVisible: !state.searchBarVisible };
    }
    case CaseActionTypes.SELECT_CASE: {
      const selectCaseAction = action as SelectCaseAction;

      return { ...state, selectedCase: { ...selectCaseAction.payload.selectedCase, refuseReasonTC: '', refuseReasonText: '' } };
    }
    case CaseActionTypes.MARK_AS_READ_SUCCESS: {
      return { ...state, selectedCase: { ...state.selectedCase, isRead: true }};
    }
    case CaseActionTypes.REFUSE_CASE_SUCCESS: {
      if (typeof action.payload === 'object' && 'selectedCase' in action.payload) {
        const payload = action.payload as SelectedCasePayload;
        return { 
          ...state, 
          cases: [...state.cases.filter(p => p.caseCode !== payload.selectedCase.caseCode)], 
          refusalModalVisible: false 
        };
      }
      return { ...state };
    }
    case CaseActionTypes.REFUSE_CASE_OPEN: {
      if (typeof action.payload === 'object' && 'selectedCase' in action.payload) { 
        return { ...state, selectedCase: { ...action.payload.selectedCase, refuseReasonTC: '', refuseReasonText: '' }, refusalModalVisible: true };
      }
      return { ...state };
    }
    case CaseActionTypes.REFUSE_CASE_CLOSE: {
      return { ...state, refusalModalVisible: false };
    }
    case CaseActionTypes.ARCHIVE_CASE_SUCCESS: {
      if (typeof action.payload === 'object' && 'selectedCase' in action.payload) {
        const payload = action.payload as SelectedCasePayload;

        return { ...state, selectedCase: {  ...state.selectedCase, archived: true }, cases: [...state.cases.filter(p => p.caseCode !== payload.selectedCase.caseCode)],  archiveModalVisible: false };
      }
      return { ...state };
    }
    case CaseActionTypes.ARCHIVE_CASE_FAIL: {
      return { ...state };
    }
    case CaseActionTypes.DEARCHIVE_CASE_SUCCESS: {
      if (typeof action.payload === 'object' && 'selectedCase' in action.payload) {
        const payload = action.payload as SelectedCasePayload;

        return { ...state,  selectedCase: {  ...state.selectedCase, archived: false }, cases: [...state.cases.filter(p => p.caseCode !== payload.selectedCase.caseCode)], archiveModalVisible: false };
      }
      return { ...state };
    }
    case CaseActionTypes.DEARCHIVE_CASE_FAIL: {
      return { ...state };
    }
    case CaseActionTypes.ARCHIVE_CASE_OPEN: {
      if (typeof action.payload === 'object' && 'selectedCase' in action.payload) { 
        return { ...state, selectedCase: { ...action.payload.selectedCase, refuseReasonTC: '', refuseReasonText: '' }, archiveModalVisible: true };
      }
      return { ...state };
    }
    case CaseActionTypes.ARCHIVE_CASE_CLOSE: {
      return { ...state, archiveModalVisible: false };
    }
    case CaseActionTypes.ASSIGN_USER_OPEN_SUCCESS: {
      if (typeof action.payload === 'object' && 'assignableUsers' in action.payload) {
        const payload = action.payload as AssignableUsersPayload;

        return {
          ...state,
          showAssignModal: true,
          assignableUsers: [...payload.assignableUsers.sort((a, b) => (a.userFullName > b.userFullName) ? 1 : -1 )],
          selectedClaimNotificationId: payload.claimNotificationId,
          selectedAssignedUserId: '',
        };
      }
      return { ...state };
    }
    case CaseActionTypes.ASSIGN_USER_OPEN_FAIL: {
      return { ...state, showAssignModal: true };
    }
    case CaseActionTypes.CHANGE_ASSIGNED_USER_ID: {
      if (typeof action.payload === 'object' && 'userId' in action.payload) {
        return { ...state, selectedAssignedUserId: action.payload.userId };
      }
      return { ...state };
    }
    case CaseActionTypes.ASSIGN_USER_CLOSE: {
      return { ...state, showAssignModal: false, selectedClaimNotificationId: 0 };
    }
    case CaseActionTypes.ASSIGN_USER_SUCCESS: {
      if (typeof action.payload === 'object' && 'claimNotificationId' in action.payload) {
        const payload = action.payload as AssignUserSuccessPayload;

        const cases = [...state.cases];
        const index = cases.findIndex(x => x.claimNotificationId === payload.claimNotificationId);
        if (index > -1) {
          const selectedCase = { ...cases[index] };
          selectedCase.expertName = payload.expertName;
          selectedCase.assignedUserId = payload.userId;
          cases[index] = selectedCase;
        }
        return { ...state, cases: cases, selectedAssignedUserId: '', selectedCase: { ...state.selectedCase, assignedUserId: payload.userId, expertName: payload.expertName } };
      }
      return { ...state };
    }
    case CaseActionTypes.REFUSE_REASON_CHANGE: {
      const refuseReasonChangeAction = action as RefuseReasonChangeAction;

      return { ...state, selectedCase: { ...state.selectedCase, refuseReasonTC: refuseReasonChangeAction.payload } };
    }
    case CaseActionTypes.REFUSE_REASON_TEXT_CHANGE: {
      const refuseReasonTextChangeAction = action as RefuseReasonTextChangeAction;

      return { ...state, selectedCase: { ...state.selectedCase, refuseReasonText: refuseReasonTextChangeAction.payload } };
    }
    case CaseActionTypes.FETCH_CASE_TYPECODES_SUCCESS: {
      const fetchCaseTypeCodesSuccessAction = action as FetchCaseTypeCodesSuccessAction;

      if (typeof action.payload === 'object' && action.payload !== null) {
        return { ...state, caseTypeCodes: { ...fetchCaseTypeCodesSuccessAction.payload } };
      }
      return { ...state };
    }
    case CaseActionTypes.FETCH_CASE_TYPECODES_FAIL: {
      return { ...state };
    }
    case CaseActionTypes.RESET_STATE: {
      return { ...initialState, caseSearch: { ...state.caseSearch }, searchBarVisible: state.searchBarVisible, requestSent: true };
    }
    case CaseActionTypes.FETCH_CASES_COUNT_SUCCESS: {
      const fetchCasesCountSuccessAction = action as FetchCasesCountSuccessAction;

      if (typeof action.payload === 'object' && action.payload !== null) {
        return { ...state, caseCount: { ...fetchCasesCountSuccessAction.payload } };
      }
      return { ...state };
    }
    case CaseActionTypes.SET_SEARCH_CASE: {
      if (typeof action.payload === 'object' && 'searchCase' in action.payload) {
        return { ...state, searchCase: action.payload.searchCase, showCaseNotFoundMessage: false };
      }
      return { ...state };
    }
    case CaseActionTypes.FETCH_PARTIES_SUCCESS: {
      if (typeof action.payload === 'object' && 'partyResponse' in action.payload) {
        const payload = action.payload as PartyResponsePayload;

        return { 
          ...state,  
          caseSearch: { 
              ...state.caseSearch, 
              parties: [ ...payload.partyResponse.sort((a, b) => a.partyName > b.partyName ? 1 : -1)]  
            } 
          };
      }
      return { ...state };
    }
    case CaseActionTypes.FETCH_CHILD_PARTIES_SUCCESS: {
      if (typeof action.payload === 'object' && 'partyResponse' in action.payload) {
        const payload = action.payload as PartyResponsePayload;

        return { ...state,  caseSearch: { ...state.caseSearch, childParties: [ ...payload.partyResponse.sort((a, b) => a.firstName > b.firstName ? 1 : -1 )]  } };
      }
      return { ...state };
    }
    case CaseActionTypes.CASE_SEARCH_CHANGE: {
      if (typeof action.payload === 'object' && 'caseSearch' in action.payload) {
        return { ...state, caseSearch: action.payload.caseSearch };
      }
      return { ...state };
    }
    case CaseActionTypes.CASE_SEARCH_RESET: {
      if (typeof action.payload === 'object' && 'caseSearch' in action.payload) {
        return { ...state, caseSearch: { ...initialState.caseSearch, parties: action.payload.caseSearch.parties, assignedParties: action.payload.caseSearch.assignedParties }};
      }
      return { ...state };
    }
    case CaseActionTypes.FETCH_ASSIGNED_PARTIES_SUCCESS: {
      if (typeof action.payload === 'object' && 'partyResponse' in action.payload) {
        const payload = action.payload as PartyResponsePayload;

        return { ...state,  caseSearch: { ...state.caseSearch, assignedParties: [ ...payload.partyResponse.sort((a, b) => a.firstName > b.firstName ? 1 : -1)]  } };
      }
      return { ...state };
    }
    case CaseActionTypes.SET_IN_PROGRESS_STATE: {
      if (typeof action.payload === 'object' && 'selectedCase' in action.payload) {
        return { ...state, selectedCase: {  ...action.payload.selectedCase, inProgress: !action.payload.selectedCase.inProgress } };
      }
      return { ...state };
    }
    case CaseActionTypes.SET_IN_PROGRESS_STATE_ERROR: {
      const inProgress = !state.selectedCase.inProgress;
      if (typeof action.payload === 'object' && 'selectedCase' in action.payload) {
        const payload = action.payload as SelectedCasePayload;

        return { ...state, selectedCase: {  ...action.payload.selectedCase, inProgress: inProgress},
          cases: state.cases.map(
              (elem, _) => elem.caseCode === payload.selectedCase.caseCode
              ? {...elem, inProgress: inProgress}
                    : elem
          )
        };
      }
      return { ...state };
    }
    case CaseActionTypes.SET_IN_PROGRESS_STATE_SUCCESS: {
      if (typeof action.payload === 'object' && 'selectedCase' in action.payload) {
        const payload = action.payload as SelectedCasePayload;

        return { ...state,
          cases: state.cases.map(
              (elem, _) => elem.caseCode === payload.selectedCase.caseCode
               ? {...elem, inProgress: state.selectedCase.inProgress}
                    : elem
          )
        }
      }
      return { ...state };
    }
    default: {
      return { ...state };
    }
  }
};

export { reducer as caseReducer, initialState as caseInitialState };
